/**
 * Pure helpers for the Saved list (F7 "Saved" segment; design frame 4e): how a row bolds its
 * selection, which translation it shows, what its ▶ plays, and when a touch drag counts as
 * swipe-to-delete. Words.tsx is thin over these.
 */
import type { SavedItem } from '@nelnela/contracts';
import type { TtsTrack } from './tts-player';

/** The sentence split around the first occurrence of `selection` (bolded in the row). */
export interface SelectionSplit {
  before: string;
  match: string;
  after: string;
}

/**
 * First occurrence wins; '' (or a selection that isn't in the sentence — the local app's
 * denormalized rows can drift) → the whole sentence is the match, per the brief.
 */
export function splitSelection(sentence: string, selection: string): SelectionSplit {
  const sel = selection.trim();
  if (!sel) return { before: '', match: sentence, after: '' };
  const i = sentence.indexOf(sel);
  if (i < 0) return { before: '', match: sentence, after: '' };
  return {
    before: sentence.slice(0, i),
    match: sentence.slice(i, i + sel.length),
    after: sentence.slice(i + sel.length),
  };
}

/** The EN line under the sentence: the denormalized translation, else the gloss snapshot's. */
export function savedTranslation(item: Pick<SavedItem, 'translation' | 'glossSnapshot'>) {
  const t = item.translation?.trim();
  if (t) return t;
  const g = item.glossSnapshot?.sentenceTranslation?.trim();
  return g || null;
}

/** Bar title = the selection when there is one, else the sentence. */
export function savedTitle(item: Pick<SavedItem, 'selection' | 'sentence'>): string {
  return item.selection.trim() || item.sentence;
}

/**
 * What ▶ on a row plays: the cached synthesized audio when the save produced one (works
 * offline/unentitled — rule 10), else null → the caller calls `speak(sentence)`.
 */
export function savedTrack(item: Pick<SavedItem, 'id' | 'selection' | 'sentence' | 'audio'>) {
  if (!item.audio) return null;
  const t: TtsTrack = {
    url: item.audio.url,
    title: savedTitle(item),
    subtitle: 'saved line · TTS',
    key: `saved:${item.id}`,
  };
  return t;
}

/** How far a row must travel leftwards before releasing counts as "reveal delete". */
export const SWIPE_REVEAL_PX = 64;
/** Where the row rests once delete is revealed (the button's width). */
export const SWIPE_OPEN_PX = 88;
/** Movement below this is a tap, not a drag (matches the transcript's 8 px). */
export const SWIPE_SLOP_PX = 8;

export type SwipeVerdict = 'none' | 'vertical' | 'horizontal';

/**
 * Axis lock for a touch drag on a row: undecided inside the slop; then whichever axis moved
 * more wins and stays won. Vertical → the list scrolls, the row doesn't move.
 */
export function swipeAxis(dx: number, dy: number, prev: SwipeVerdict): SwipeVerdict {
  if (prev !== 'none') return prev;
  if (Math.abs(dx) < SWIPE_SLOP_PX && Math.abs(dy) < SWIPE_SLOP_PX) return 'none';
  return Math.abs(dy) > Math.abs(dx) ? 'vertical' : 'horizontal';
}

/** The row's translateX while dragging: only leftwards, rubber-stopped at the open width. */
export function swipeOffset(dx: number): number {
  if (dx >= 0) return 0;
  return Math.max(-SWIPE_OPEN_PX, dx);
}

/** On release: past the reveal threshold → stay open (delete showing), else snap shut. */
export function swipeSettles(dx: number): 'open' | 'closed' {
  return dx <= -SWIPE_REVEAL_PX ? 'open' : 'closed';
}
